// animations/common-base-animations.ts
import gsap from 'gsap';

/**
 * Base fade animation - can be used for entrance, hover, click
 */
export function baseFade(el: Element, opts: gsap.TweenVars) {
  return gsap.fromTo(el, { opacity: 0 }, { opacity: 1, ...opts });
}

/**
 * Base flip animation (Y-axis rotation)
 */
export function baseFlip(el: Element, opts: gsap.TweenVars & { direction?: string }) {
    const direction = opts.direction?.toLowerCase() || 'right';
    
    const directionMap: Record<string, { axis: string; angle: number }> = {
      'right': { axis: 'rotationY', angle: 180 },
      'left':  { axis: 'rotationY', angle: -180 },
      'up':    { axis: 'rotationX', angle: 180 },
      'down':  { axis: 'rotationX', angle: -180 }
    };
    
    const flipConfig = directionMap[direction] || directionMap['right'];
    const { direction: _, ...cleanOpts } = opts; // remove direction from opts
    
    return gsap.fromTo(el, 
      { [flipConfig.axis]: flipConfig.angle, opacity: 0 }, 
      { [flipConfig.axis]: 0, opacity: 1, ...cleanOpts }
    );
  }

/**
 * Base flap animation (X-axis rotation)  
 */
export function baseFlap(el: Element, opts: gsap.TweenVars) {
  return gsap.fromTo(el, { rotationX: 90 }, { rotationX: 0, ...opts });
}

/**
 * Base bounce animation
 */
export function baseBounce(el: Element, opts: gsap.TweenVars) {
  return gsap.from(el, { y: -100, ease: 'bounce.out', ...opts });
}

/**
 * Base expand animation  
 */
export function baseExpand(
    el: Element,
    opts: gsap.TweenVars & { fromScale?: number }
  ) {
    const fromScale = opts.fromScale ?? 0;
    const { fromScale: _, ...cleanOpts } = opts;
    return gsap.fromTo(
      el,
      { scale: fromScale },
      { scale: cleanOpts.scale ?? 1, ...cleanOpts }
    );
  }

/**
 * Base shrink animation
 */
export function baseShrink(el: Element, opts: gsap.TweenVars) {
  return gsap.fromTo(el, { scale: 1 }, { scale: 0, ...opts });
}

/**
 * Base skew animation
 */
export function baseSkew(el: Element, opts: gsap.TweenVars) {
  return gsap.to(el, { skewX: 20, ...opts });
}

/**
 * Base rotate animation
 */
export function baseRotate(el: Element, opts: gsap.TweenVars) {
  return gsap.to(el, { rotation: 360, ...opts });
}

/**
 * Base move animation
 */
export function baseMove(el: Element, opts: gsap.TweenVars) {
  return gsap.to(el, { x: 100, y: 0, ...opts });
}

/**
 * Base twist animation (rotate + scale)
 */  
export function baseTwist(el: Element, opts: gsap.TweenVars) {
  return gsap.fromTo(el, { rotation: 0, scale: 1 }, { rotation: 180, scale: 1.2, ...opts });
}

/**
 * Base appear animation (opacity change)
 */
export function baseAppear(el: Element, opts: gsap.TweenVars) {
  return gsap.fromTo(el, { opacity: 0 }, { opacity: 1, ...opts });
}

// export function baseClickExpand(el: Element, opts: gsap.TweenVars) {
//   return gsap.fromTo(el,
//     { scale: 1 },
//     { scale: opts.scale ?? 1.5, duration: opts.duration ?? 0.5, ease: opts.ease ?? 'back.out(1.7)', ...opts }
//   );
// }

/**
 * Base color animation
 */
// export function baseHover(el: Element, opts: gsap.TweenVars & { hoverColor?: string }) {
//   const currentColor = window.getComputedStyle(el).color;
//   const hoverColor = opts.hoverColor ?? '#222222';
//   const { hoverColor: _, ...cleanOpts } = opts;
//   return gsap.fromTo(el, { color: currentColor }, { color: hoverColor, ...cleanOpts });
// }